import moment from 'moment/src/moment';
import { store } from '../../store';
import * as app from '../../redux/app';

/**
 * Formats a given timestamp into a human-readable string.
 *
 * If the timestamp is within the last 7 days, it returns the date and time in the format "MMM DD, YYYY, hh:mm A".
 * Otherwise, it returns the date in the format "MMM DD, YYYY".
 *
 * @param {number|string} timestamp - The timestamp to format. It can be a number, string.
 * @returns {string} The formatted date string.
 */
export const getFormatDate = (timestamp) => {
  const date = moment(timestamp);
  const isLast7Days = calculateDaysRemaining(timestamp) <= 7;
  return isLast7Days ? date?.format('MMM DD, YYYY, hh:mm A') : date?.format('MMM DD, YYYY');
};

// Common currency symbols that might not be properly supported by Intl API
const currencySymbols = {
  'AFN': '؋',
  'ARS': '$',
  'AWG': 'ƒ',
  'BDT': '৳',
  'BHD': '.د.ب',
  'BTC': '₿',
  'CRC': '₡',
  'IQD': 'ع.د',
  'IRR': '﷼',
  'KHR': '៛',
  'KPW': '₩',
  'LAK': '₭',
  'LBP': 'ل.ل',
  'MNT': '₮',
  'NGN': '₦',
  'SYP': '£',
  'THB': '฿',
  'VND': '₫'
};

/**
 * Returns the currency symbol for a given currency code.
 * Uses the browser's Intl API to get localized currency symbols.
 * 
 * @param {string} currencyCode - The ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @param {string} [locale='en-US'] - The locale to use for formatting
 * @returns {string} The currency symbol or the original currency code if not found
 */
export const getCurrencySymbol = (currencyCode, locale = 'en-US') => {
  if (!currencyCode) return '';
  currencyCode = currencyCode.toUpperCase();
  
  try {
    // Create a formatter with the specified currency
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    
    // Format a 0 value and extract just the symbol
    const parts = formatter.formatToParts(0);
    const currencySymbol = parts.find(part => part.type === 'currency')?.value || currencyCode;
    
    // If the extracted symbol is the same as the currency code, it means the browser didn't find a symbol
    if (currencySymbol === currencyCode) {
      return currencySymbols[currencyCode] || currencyCode;
    }
    
    return currencySymbol;
  } catch (error) {
    // If the currency code is invalid, return the original code
    return currencySymbols[currencyCode] || currencyCode;
  }
};

/**
 * Utility function to calculate days remaining until a specified date
 * @param {Number|String} endDate - The end date timestamp or string
 * @returns {Number} - Days remaining (ceiling value to ensure we don't show 0 days when there's still time left)
 */
export const calculateDaysRemaining = (endDate) => {
  if (!endDate) return 0;
  
  // Use moment's precise diff calculation to get exact days remaining
  const now = moment();
  const endTime = moment(endDate);
  
  // Calculate the exact difference in days (including fractional days)
  const diffInDays = endTime.diff(now, 'hours') / 24;
  
  // Ceil the result to ensure we don't show 0 days when there's still time left
  return Math.ceil(diffInDays);
}

/**
 * Formats a number as a locale string with specified decimal place configuration.
 * 
 * @param {number|null|undefined} number - The number to format
 * @param {string} [lang='en'] - The locale code to use for formatting (e.g., 'en', 'fr', 'de')
 * @param {number} [minimumFractionDigits=2] - Minimum number of fraction digits to use
 * @param {number} [maximumFractionDigits=2] - Maximum number of fraction digits to use
 * @returns {string} The formatted number as a string according to the specified locale
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString|MDN toLocaleString}
 */
export const getLocaleString = (number, lang = 'en', minimumFractionDigits = 2, maximumFractionDigits = 2) => {
  // Handle null, undefined, 0, NaN, and other falsy values
  if (number === null || number === undefined || isNaN(number)) {
    return (0).toLocaleString(lang, { minimumFractionDigits, maximumFractionDigits });
  }
  
  const options = { 
    minimumFractionDigits, 
    maximumFractionDigits 
  };
  
  return Number(number).toLocaleString(lang, options);
}

/**
 * Converts a given amount to a formatted currency string based on the currency code.
 * Handles zero-decimal currencies (like JPY) differently from standard currencies.
 * 
 * For zero-decimal currencies, the amount is displayed as is.
 * For standard currencies, the amount is divided by 100 (assuming amounts are stored in cents).
 *
 * @param {number|null|undefined} amount - The amount to convert
 * @param {string} currencyCode - The ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @param {string} [lang='en'] - The locale to use for number formatting
 * @param {boolean} [formatWithLocale=true] - Whether to format the result with locale string or return as a raw number
 * @returns {string|number} The formatted currency amount as a string if formatWithLocale is true, or as a number if false
 */
export const getCurrencyAmount = (amount, currencyCode = 'USD', formatWithLocale = true, lang = 'en') => {
  // Early return for invalid inputs
  if (amount === null || amount === undefined || !currencyCode) {
    return formatWithLocale ? '' : 0;
  }
  
  // Get zero-decimal currencies from config
  const config = app.selectors.config(store.getState());
  let stripeZeroDecimalCurrencies = config?.stripeZeroDecimalCurrencies || [];
  if (typeof stripeZeroDecimalCurrencies === 'string') {
    stripeZeroDecimalCurrencies = stripeZeroDecimalCurrencies.split(',');
  }
  
  // Standardize currency code
  const normalizedCurrencyCode = currencyCode.toUpperCase();
  
  // Calculate the numeric value based on whether it's a zero-decimal currency
  const numericValue = stripeZeroDecimalCurrencies.includes(normalizedCurrencyCode) 
    ? amount 
    : amount / 100;
  
  // Return either the raw numeric value or the formatted locale string
  return formatWithLocale 
    ? getLocaleString(numericValue, lang)
    : numericValue;
}

/**
 * Converts a plan name string to uppercase with spaces replaced by underscores
 * @param {String} name - The plan name to format
 * @returns {String} - Formatted plan name (e.g. "test enterprise" -> "TEST_ENTERPRISE")
 */
export const formatPlanName = (name) => {
  if (!name) return '';
  return name.toUpperCase().replace(/\s+/g, '_');
}